/**
 * L'état d'une séance en direct (`ARCHITECTURE_V2.md` §3.3, §3.5 et §4.5).
 *
 * ─────────────────────────────────────────────────────────────────────────
 * 🔴 L'APERÇU OPTIMISTE EST ÉCRIT DANS LE CACHE, PAS DANS UN `useState`
 * ─────────────────────────────────────────────────────────────────────────
 *
 * C'est **le** point de cette étape, et il porte à lui seul le critère du §8.6
 * — « une séance complète en mode avion, application **fermée et rouverte au
 * milieu**, remontée intégralement ».
 *
 * Une séance commencée hors ligne n'existe à ce moment-là que dans deux
 * endroits : la file de synchronisation (`nutri-sync`), qui est une file
 * d'**opérations** et non un état lisible, et le cache de lecture
 * (`nutri-cache`), qui est **persisté en IndexedDB** (§3.2). Un `useState`
 * meurt avec l'onglet ; un `setQueryData` survit à la fermeture de
 * l'application et au redémarrage de l'appareil.
 *
 * D'où la règle de ce fichier : **chaque mutation réécrit la séance dans le
 * cache avant même de partir**, et la réponse du serveur — quand elle
 * arrive — ne fait que remplacer cette écriture par la sienne.
 *
 * ⚠️ **Et l'échec de transport ne défait rien** (§3.5). C'est le rollback
 * scindé de `useDay.ts` : hors ligne, une mutation lève `QueuedError` alors
 * qu'elle est parfaitement en route. Défaire l'aperçu à ce moment ferait
 * disparaître de l'écran une série pourtant en file — le pire des deux mondes,
 * et exactement ce que le hors ligne existe pour empêcher. Seule une erreur
 * **applicative** (le serveur a répondu et refuse) annule.
 *
 * ⚠️ **La liste des séances est réécrite en même temps que la séance.** Sans
 * cela, revenir à l'historique après avoir validé trois séries montrerait la
 * séance telle qu'elle était en arrivant — et il n'y a aucun aller-retour à
 * attendre pour la corriger, puisqu'on tient déjà la valeur.
 */

import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import type { ActualSetInput, SessionInput, WorkoutSessionDTO } from '../../../shared/types';
import { isTransportFailure } from '../api/client';
import {
  SESSIONS_KEY,
  deleteSession,
  deleteSessionNote,
  deleteSet,
  fetchSession,
  putSessionNote,
  putSession,
  putSet,
  sessionKey,
} from '../api/sessions';

/* ==========================================================================
 * Écriture dans le cache
 * ======================================================================= */

/**
 * Range une séance aux **deux** endroits qui la connaissent.
 *
 * ⚠️ La liste est bornée à trente par le serveur ; on ne la tronque pas ici.
 * Une séance de plus dans une liste locale ne coûte rien, et la prochaine
 * lecture serveur remettra la fenêtre en place — alors qu'élaguer nous-mêmes
 * dupliquerait une règle qui appartient au serveur.
 */
export function writeSession(client: QueryClient, session: WorkoutSessionDTO): void {
  client.setQueryData(sessionKey(session.clientId), session);
  client.setQueryData<WorkoutSessionDTO[]>(SESSIONS_KEY, (liste) => {
    if (!liste) return [session];
    const connu = liste.some((row) => row.clientId === session.clientId);
    return connu
      ? liste.map((row) => (row.clientId === session.clientId ? session : row))
      : [session, ...liste];
  });
}

/**
 * Applique une série au cache **sans attendre le serveur**.
 *
 * ⚠️ **Remplace ou ajoute, selon le `clientId`** — jamais « la dernière de la
 * liste ». C'est la leçon de `MealDTO.clientId` à l'étape 3 : une devinette qui
 * se trompe écrit dans la mauvaise ligne, et ici elle écrirait une charge sur
 * la mauvaise série.
 *
 * ⚠️ **Réordonné par `position`** : une série ajoutée en cours de séance porte
 * une position plus haute que le plan, et doit se ranger là où elle sera faite,
 * pas là où elle a été créée.
 */
function applySet(
  session: WorkoutSessionDTO,
  setClientId: string,
  body: ActualSetInput,
  media?: { mediaKey: string | null; imageCount: number },
): WorkoutSessionDTO {
  const existante = session.sets.find((row) => row.clientId === setClientId);
  const fusion = {
    /*
     * ⚠️ Les champs que le serveur seul connaît sont **conservés** quand la
     * série existe déjà (son `id`, la vignette de l'exercice), et pris à zéro
     * quand elle est neuve. L'aperçu n'invente rien : il n'affiche que ce qu'il
     * sait, et la réponse du serveur comble le reste.
     */
    id: existante?.id ?? 0,
    exerciseClientId: body.exerciseClientId,
    exerciseName: body.exerciseName,
    /*
     * 🔴 **La vignette vient de l'appelant quand la série est neuve.** Sans
     * elle, une série ajoutée hors ligne resterait sans image jusqu'à la
     * première réponse du serveur — c'est-à-dire jamais. Défaut trouvé à
     * l'usage : « les vignettes ne s'affichent qu'après avoir validé la
     * première série ».
     *
     * ⚠️ Ce que la série connaît déjà **gagne** : une réponse du serveur a pu
     * la renseigner entre-temps, et l'appelant, lui, ne fait que proposer.
     */
    exerciseMediaKey: existante?.exerciseMediaKey ?? media?.mediaKey ?? null,
    exerciseImageCount: existante?.exerciseImageCount ?? media?.imageCount ?? 0,
    clientId: setClientId,
    blockKey: body.blockKey ?? null,
    position: body.position,
    setIndex: body.setIndex,
    setKind: body.setKind ?? 'work',
    reps: body.reps ?? null,
    durationSeconds: body.durationSeconds ?? null,
    weightKg: body.weightKg ?? null,
    rpe: body.rpe ?? null,
    status: body.status ?? 'pending',
    targetRepsMin: body.targetRepsMin ?? null,
    targetRepsMax: body.targetRepsMax ?? null,
    targetDurationSeconds: body.targetDurationSeconds ?? null,
    targetWeightKg: body.targetWeightKg ?? null,
    targetRpe: body.targetRpe ?? null,
  };

  const sets = existante
    ? session.sets.map((row) => (row.clientId === setClientId ? fusion : row))
    : [...session.sets, fusion];

  return { ...session, sets: [...sets].sort((a, b) => a.position - b.position) };
}

/* ==========================================================================
 * Le hook
 * ======================================================================= */

export function useSession(clientId: string) {
  const queryClient = useQueryClient();

  /**
   * ⚠️ **`staleTime` généreux, et c'est délibéré.** Une séance en cours est
   * écrite par cet écran et par personne d'autre : la relire toutes les
   * secondes ne peut qu'écraser un aperçu plus frais par une réponse plus
   * ancienne. Le cache est ici la source, le serveur la destination.
   *
   * ⚠️ **Une séance créée hors ligne n'existe pas encore côté serveur**, donc
   * cette requête peut très bien répondre 404 pendant des heures. C'est sans
   * conséquence : l'écran n'affiche une erreur que s'il n'a **rien** à montrer
   * (la règle du §3.5 — « ai-je quelque chose à montrer ? », jamais « y
   * a-t-il une erreur ? »), et le cache, lui, a tout.
   */
  const query = useQuery({
    queryKey: sessionKey(clientId),
    queryFn: () => fetchSession(clientId),
    staleTime: 30_000,
  });

  const session = query.data ?? null;

  /** Réécrit la séance à partir de ce que le serveur vient de confirmer. */
  const settle = useCallback(
    (fresh: WorkoutSessionDTO) => {
      writeSession(queryClient, fresh);
    },
    [queryClient],
  );

  /**
   * Écrire une série **périme le rappel des AUTRES séances** (V2 étape 7).
   *
   * 🔴 **C'est ce qui rend « corriger une série passée change la proposition »
   * vrai *immédiatement*.** Rien n'est stocké — la règle se calcule à chaque
   * lecture (§4.4) —, mais la *réponse* de `GET .../last`, elle, est en cache
   * avec un `staleTime` de cinq minutes. Sans cette ligne, corriger une charge
   * de la semaine dernière laisserait l'écran servir la proposition d'avant la
   * correction, alors que le serveur, lui, aurait déjà changé d'avis.
   *
   * ⚠️ **Ce n'est PAS une invalidation de valeur matérialisée**, et la nuance
   * est tout le §4.4 : il n'existe aucune charge proposée à recalculer quelque
   * part. On périme une *copie de réponse HTTP*, exactement comme n'importe
   * quelle lecture après n'importe quelle écriture.
   *
   * 🔴 **Sauf les entrées de CETTE séance, et c'est le point délicat.** Une
   * séance s'exclut elle-même de son propre dernier passage : ses écritures ne
   * peuvent donc pas changer sa propre réponse. Les périmer ferait repartir
   * huit requêtes **à chaque coche**, c'est-à-dire trente fois par séance, dans
   * l'endroit précis où il n'y a pas de réseau. Le prédicat lit la séance
   * exclue dans la clé (`['exercise-last', exo, exclude, routine]`).
   *
   * ⚠️ **L'historique de l'exercice suit**, pour la même raison : c'est la même
   * requête à un `limit` près, et sa courbe de charge doit bouger avec la
   * correction qui l'a provoquée.
   */
  const perimerRappels = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ['exercise-last'],
      predicate: (query) => query.queryKey[2] !== clientId,
    });
    queryClient.invalidateQueries({ queryKey: ['exercise-history'] });
  }, [queryClient, clientId]);

  const settleEtPerimer = useCallback(
    (fresh: WorkoutSessionDTO) => {
      writeSession(queryClient, fresh);
      perimerRappels();
    },
    [queryClient, perimerRappels],
  );

  /**
   * 🔴 **Un échec de transport ne défait rien** — cf. l'en-tête. Le contexte
   * porte l'état d'avant, et on ne le repose que si le serveur a répondu.
   */
  const onError = useCallback(
    (error: Error, _variables: unknown, avant: WorkoutSessionDTO | null | undefined) => {
      if (isTransportFailure(error)) return;
      if (avant) writeSession(queryClient, avant);
    },
    [queryClient],
  );

  const saveHeader = useMutation({
    mutationFn: (body: SessionInput) => putSession(clientId, body),
    onMutate: (body: SessionInput) => {
      const avant = queryClient.getQueryData<WorkoutSessionDTO>(sessionKey(clientId)) ?? null;
      if (avant) {
        writeSession(queryClient, {
          ...avant,
          date: body.date,
          routineClientId: body.routineClientId ?? null,
          routineName: body.routineName ?? avant.routineName,
          startedAt: body.startedAt,
          finishedAt: body.finishedAt ?? null,
          notes: body.notes ?? null,
        });
      }
      return avant;
    },
    onSuccess: settle,
    onError,
  });

  const saveSet = useMutation({
    mutationFn: ({
      setClientId,
      body,
    }: {
      setClientId: string;
      body: ActualSetInput;
      /** Vignette pour l'aperçu — jamais envoyée au serveur (§4.1). */
      media?: { mediaKey: string | null; imageCount: number };
    }) => putSet(clientId, setClientId, body),
    onMutate: ({ setClientId, body, media }) => {
      const avant = queryClient.getQueryData<WorkoutSessionDTO>(sessionKey(clientId)) ?? null;
      if (avant) writeSession(queryClient, applySet(avant, setClientId, body, media));
      return avant;
    },
    onSuccess: settleEtPerimer,
    onError,
  });

  const removeSet = useMutation({
    mutationFn: (setClientId: string) => deleteSet(clientId, setClientId),
    onMutate: (setClientId: string) => {
      const avant = queryClient.getQueryData<WorkoutSessionDTO>(sessionKey(clientId)) ?? null;
      if (avant) {
        writeSession(queryClient, {
          ...avant,
          sets: avant.sets.filter((row) => row.clientId !== setClientId),
        });
      }
      return avant;
    },
    /*
     * ⚠️ **Le `DELETE` ne renvoie pas la séance** (204), contrairement aux
     * `PUT` : l'aperçu optimiste **est** l'état final, il n'y a rien à
     * réconcilier. C'est la seule mutation de ce fichier dans ce cas.
     *
     * ⚠️ **Mais le rappel se périme quand même** : retirer une série d'une
     * séance passée change ce que cette séance raconte, donc la proposition
     * suivante. Rien à réconcilier ne veut pas dire rien à périmer.
     */
    onSuccess: perimerRappels,
    onError,
  });

  /**
   * Supprimer la séance entière — demandé après usage *(29/08/2026)*.
   *
   * ⚠️ **Elle sort des DEUX entrées de cache**, pas seulement de la liste :
   * l'oublier dans `['session', clientId]` laisserait l'écran la réafficher au
   * retour, restaurée depuis IndexedDB, sur une séance qui n'existe plus côté
   * serveur. C'est exactement le genre de fantôme que la persistance rend
   * possible et qu'elle oblige à traiter.
   */
  const removeSession = useMutation({
    mutationFn: () => deleteSession(clientId),
    onMutate: () => {
      const avant = queryClient.getQueryData<WorkoutSessionDTO>(sessionKey(clientId)) ?? null;
      queryClient.removeQueries({ queryKey: sessionKey(clientId) });
      queryClient.setQueryData<WorkoutSessionDTO[]>(SESSIONS_KEY, (liste) =>
        liste?.filter((row) => row.clientId !== clientId),
      );
      return avant;
    },
    onError,
  });

  /**
   * Supprimer la note d'un exercice.
   *
   * ⚠️ **C'est ce que fait un champ vidé** (§4.5 : le champ de note est
   * éditable en place depuis le 29/08). Écrire une note vide laisserait en base
   * une ligne qui n'affiche rien, et `notesByExercise` en porterait autant que
   * de champs ouverts puis quittés.
   */
  const removeNote = useMutation({
    mutationFn: (exerciseClientId: string) => deleteSessionNote(clientId, exerciseClientId),
    onMutate: (exerciseClientId: string) => {
      const avant = queryClient.getQueryData<WorkoutSessionDTO>(sessionKey(clientId)) ?? null;
      if (avant) {
        writeSession(queryClient, {
          ...avant,
          notesByExercise: avant.notesByExercise.filter(
            (row) => row.exerciseClientId !== exerciseClientId,
          ),
        });
      }
      return avant;
    },
    onError,
  });

  const saveNote = useMutation({
    mutationFn: ({ exerciseClientId, note }: { exerciseClientId: string; note: string }) =>
      putSessionNote(clientId, exerciseClientId, { note }),
    onMutate: ({ exerciseClientId, note }) => {
      const avant = queryClient.getQueryData<WorkoutSessionDTO>(sessionKey(clientId)) ?? null;
      if (avant) {
        const connue = avant.notesByExercise.some(
          (row) => row.exerciseClientId === exerciseClientId,
        );
        writeSession(queryClient, {
          ...avant,
          notesByExercise: connue
            ? avant.notesByExercise.map((row) =>
                row.exerciseClientId === exerciseClientId ? { ...row, note } : row,
              )
            : [...avant.notesByExercise, { exerciseClientId, note }],
        });
      }
      return avant;
    },
    onSuccess: settle,
    onError,
  });

  return {
    session,
    query,
    saveHeader,
    saveSet,
    removeSet,
    removeSession,
    saveNote,
    removeNote,
    /**
     * ⚠️ **Une seule erreur applicative à afficher**, celle de la dernière
     * mutation qui a réellement été refusée. Les échecs de transport n'en font
     * pas partie : hors ligne est un **mode nominal** (§3.5), et un bandeau
     * rouge sur une séance qui se déroule parfaitement serait un mensonge.
     */
    mutationError:
      [
        saveHeader.error,
        saveSet.error,
        removeSet.error,
        removeSession.error,
        saveNote.error,
        removeNote.error,
      ].find(
        (error): error is Error => error !== null && !isTransportFailure(error),
      ) ?? null,
  };
}
